import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '../ui/dialog';
import { Textarea } from '../ui/textarea';

interface Transition {
  id: string;
  toStageId: string;
  name: string;
  requiresNote: boolean;
}

interface Stage {
  id: string;
  slug: string;
  name: string;
  color: string;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  leadTitle: string;
  outgoingTransitions: Transition[];
  stagesById: Record<string, Stage>;
  onMove: (input: { transitionId: string; note?: string }) => Promise<unknown>;
  busy?: boolean;
}

export function MoveLeadSheet({
  open,
  onOpenChange,
  leadTitle,
  outgoingTransitions,
  stagesById,
  onMove,
  busy,
}: Props): JSX.Element {
  const { t } = useTranslation();
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pickedTransitionId, setPickedTransitionId] = useState<string | null>(null);

  const picked = pickedTransitionId
    ? outgoingTransitions.find((tr) => tr.id === pickedTransitionId)
    : null;

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          setNote('');
          setError(null);
          setPickedTransitionId(null);
        }
        onOpenChange(o);
      }}
    >
      <DialogContent>
        <DialogTitle>{t('lead.movePromptTitle', { title: leadTitle })}</DialogTitle>
        <DialogDescription>{t('lead.movePromptSubtitle')}</DialogDescription>
        {!picked ? (
          <ul className="mt-4 space-y-2">
            {outgoingTransitions.length === 0 && (
              <li className="text-sm text-surface-500">{t('lead.terminalState')}</li>
            )}
            {outgoingTransitions.map((tr) => {
              const target = stagesById[tr.toStageId];
              return (
                <li key={tr.id}>
                  <button
                    type="button"
                    className="flex w-full items-center justify-between rounded-md border border-surface-200 px-3 py-2 text-left hover:bg-surface-50"
                    onClick={() => setPickedTransitionId(tr.id)}
                  >
                    <span className="text-sm font-medium">{tr.name}</span>
                    <span className="flex items-center gap-2 text-xs text-surface-500">
                      <span
                        className="h-2 w-2 rounded-full"
                        style={{ backgroundColor: target?.color }}
                      />
                      {target?.name ?? tr.toStageId}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        ) : (
          <div className="mt-4 space-y-3">
            <p className="text-sm">
              <Trans
                i18nKey="lead.movingTo"
                values={{ to: stagesById[picked.toStageId]?.name ?? '', transition: picked.name }}
                components={{ strong: <strong />, em: <em /> }}
              />
            </p>
            {picked.requiresNote ? (
              <Textarea
                placeholder={t('lead.noteRequired')}
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            ) : (
              <Textarea
                placeholder={t('lead.noteOptional')}
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            )}
            {error && <p className="text-sm text-red-600">{error}</p>}
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setPickedTransitionId(null)} disabled={busy}>
                {t('common.back')}
              </Button>
              <Button
                disabled={busy || (picked.requiresNote && !note.trim())}
                onClick={async () => {
                  setError(null);
                  try {
                    await onMove({
                      transitionId: picked.id,
                      ...(note.trim() && { note: note.trim() }),
                    });
                    onOpenChange(false);
                  } catch (e) {
                    setError(e instanceof Error ? e.message : t('lead.moveFailed'));
                  }
                }}
              >
                {busy ? t('lead.moving') : t('lead.confirmMove')}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
